import { ref } from 'vue';
import api from '@/services/api';

interface User {
  id: string;
  username: string;
  city: string;
}

interface UsersMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface UsersResponse {
  success: boolean;
  data: User[];
  meta: UsersMeta;
}

interface Toast {
  show: boolean;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
}

export function useFollowedUsers() {
  const users = ref<User[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const meta = ref<UsersMeta>({
    total: 0,
    page: 1,
    limit: 12,
    totalPages: 1,
  });
  const toast = ref<Toast>({
    show: false,
    message: '',
    type: 'info',
  });

  /**
   * Show a toast notification
   */
  const showToast = (
    message: string,
    type: 'info' | 'success' | 'warning' | 'error' = 'info'
  ) => {
    toast.value = {
      show: true,
      message,
      type,
    };

    // Automatically hide toast after 3 seconds
    setTimeout(() => {
      toast.value.show = false;
    }, 3000);
  };

  /**
   * Fetch followed users with pagination
   */
  const fetch = async (page: number = 1) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.get<UsersResponse>(
        `/api/user-follows/following?page=${page}`
      );

      if (response.data.success) {
        users.value = response.data.data;
        meta.value = response.data.meta;
      } else {
        error.value = 'Greška prilikom učitavanja praćenih korisnika.';
      }
    } catch (err) {
      console.error('Error fetching followed users:', err);
      error.value = 'Greška prilikom komunikacije sa serverom.';
    } finally {
      loading.value = false;
    }
  };

  /**
   * Unfollow a user with smart pagination handling
   */
  const unfollowWithPagination = async (
    userId: string,
    currentPage: number,
    onPageChange: (page: number) => void
  ) => {
    try {
      // Check if this is the last item on current page and we're not on page 1
      const isLastItemOnPage = users.value.length === 1;
      const isOnPageGreaterThan1 = currentPage > 1;

      await api.delete(`/api/user-follows/users/${userId}/unfollow`);

      // Remove user from list
      users.value = users.value.filter((user) => user.id !== userId);

      // Update meta data
      if (meta.value.total > 0) {
        meta.value.total -= 1;
        meta.value.totalPages = Math.ceil(meta.value.total / meta.value.limit);
      }

      // Handle pagination logic
      if (isLastItemOnPage && isOnPageGreaterThan1) {
        // Go to previous page and fetch data
        const newPage = currentPage - 1;
        onPageChange(newPage);
        await fetch(newPage);
      }

      showToast('Korisnik je uspešno otpraćen', 'success');
    } catch (err) {
      console.error('Error unfollowing user:', err);
      showToast('Greška prilikom otpraćivanja korisnika', 'error');
    }
  };

  /**
   * Regular unfollow without pagination handling (for backward compatibility)
   */
  const unfollow = async (userId: string) => {
    try {
      await api.delete(`/api/user-follows/users/${userId}/unfollow`);

      // Remove user from list
      users.value = users.value.filter((user) => user.id !== userId);

      // Update meta data
      if (meta.value.total > 0) {
        meta.value.total -= 1;
        meta.value.totalPages = Math.ceil(meta.value.total / meta.value.limit);
      }

      showToast('Korisnik je uspešno otpraćen', 'success');
    } catch (err) {
      console.error('Error unfollowing user:', err);
      showToast('Greška prilikom otpraćivanja korisnika', 'error');
    }
  };

  return {
    users,
    loading,
    error,
    meta,
    toast,
    fetch,
    unfollow,
    unfollowWithPagination,
    showToast,
  };
}
